import { useState } from 'react';
import { Phone, Mail, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { imageUrls } from '../utils/imageOptimizer';
import SEO from '../components/SEO';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Der opstod en fejl');
      }
      
      setIsSubmitted(true);
      setFormData({
        name: '',
        company: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      console.error('Contact form error:', error);
      alert(error.message || 'Der opstod en fejl. Prøv venligst igen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-24">
      <SEO 
        title="Kontakt"
        pathname="/kontakt"
        description="Kontakt NeonSign for en uforpligtende snak om dit projekt. Ring +45 36 30 80 80 eller besøg os på Symfonivej 35, 2730 Herlev. Vi svarer inden for 24 timer."
      />
      {/* Hero with Image */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <p className="text-[#999999] text-xs tracking-[0.2em] uppercase mb-6 animate-fade-in">
                Kontakt
              </p>
              <h1 
                data-testid="contact-title"
                className="font-heading text-4xl md:text-5xl font-light text-[#1A1A1A] mb-8 animate-fade-in-up"
              >
                Lad os tale om<br />dit projekt
              </h1>
              <p className="text-[#666666] text-lg animate-fade-in-up stagger-2">
                Vi er klar til at hjælpe dig med at finde den perfekte løsning.
              </p>
            </div>
            <div className="image-hover animate-fade-in-up stagger-3">
              <img
                src={imageUrls.large('https://customer-assets.emergentagent.com/job_82f36689-5905-4eb9-a410-52f80a0daaf2/artifacts/cdj6isap_251206_NEONSIGN_CHP-197.jpg')}
                alt="NeonSign projekt"
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Sections */}
      <section className="bg-[#F8F8F8] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {/* Kontakt for samarbejde */}
            <div className="bg-white p-8 md:p-10">
              <h2 className="font-heading text-xl font-medium text-[#1A1A1A] mb-6">
                Kontakt for samarbejde
              </h2>
              <p className="text-[#666666] text-sm leading-relaxed mb-6">
                Er du arkitekt, designer eller bureau? Kontakt os for at høre mere om hvordan vi kan være jeres produktionspartner.
              </p>
              <div className="space-y-4">
                <a
                  href="mailto:mc@neonsign.dk"
                  className="flex items-center gap-3 text-[#1A1A1A] hover:text-[#666666] transition-colors"
                >
                  <Mail className="w-4 h-4 text-[#999999]" />
                  mc@neonsign.dk
                </a>
                <a
                  href="tel:+4536308080"
                  className="flex items-center gap-3 text-[#1A1A1A] hover:text-[#666666] transition-colors"
                >
                  <Phone className="w-4 h-4 text-[#999999]" />
                  +45 36 30 80 80
                </a>
              </div>
            </div>

            {/* Kontakt til produktion */}
            <div className="bg-white p-8 md:p-10">
              <h2 className="font-heading text-xl font-medium text-[#1A1A1A] mb-6">
                Kontakt til produktion
              </h2>
              <p className="text-[#666666] text-sm leading-relaxed mb-6">
                Har du spørgsmål til en igangværende ordre eller produktion? Kontakt vores produktionsteam direkte.
              </p>
              <div className="space-y-4">
                <a
                  href="mailto:tc@neonsign.dk"
                  className="flex items-center gap-3 text-[#1A1A1A] hover:text-[#666666] transition-colors"
                >
                  <Mail className="w-4 h-4 text-[#999999]" />
                  tc@neonsign.dk
                </a>
                <a
                  href="tel:+4536308080"
                  className="flex items-center gap-3 text-[#1A1A1A] hover:text-[#666666] transition-colors"
                >
                  <Phone className="w-4 h-4 text-[#999999]" />
                  +45 36 30 80 80
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form & Address */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Contact Form */}
            <div className="bg-white p-8 md:p-12">
              <h2 className="font-heading text-xl font-medium text-[#1A1A1A] mb-8">
                Send os en besked
              </h2>
              {isSubmitted ? (
                <div data-testid="form-success" className="text-center py-12">
                  <h3 className="font-heading text-2xl font-light text-[#1A1A1A] mb-4">
                    Tak for din henvendelse
                  </h3>
                  <p className="text-[#666666] mb-8">
                    Vi har modtaget din besked og vender tilbage hurtigst muligt.
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="text-[#1A1A1A] underline text-sm"
                  >
                    Send en ny besked
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-[#1A1A1A] text-xs tracking-[0.1em] uppercase">Navn *</Label>
                      <Input
                        id="name"
                        name="name"
                        data-testid="input-name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="bg-transparent border-0 border-b border-[#E5E5E5] rounded-none px-0 focus:border-[#1A1A1A] focus:ring-0 text-[#1A1A1A] placeholder:text-[#999999]"
                        placeholder="Dit navn"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company" className="text-[#1A1A1A] text-xs tracking-[0.1em] uppercase">Virksomhed</Label>
                      <Input
                        id="company"
                        name="company"
                        data-testid="input-company"
                        value={formData.company}
                        onChange={handleChange}
                        className="bg-transparent border-0 border-b border-[#E5E5E5] rounded-none px-0 focus:border-[#1A1A1A] focus:ring-0 text-[#1A1A1A] placeholder:text-[#999999]"
                        placeholder="Virksomhedsnavn"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-[#1A1A1A] text-xs tracking-[0.1em] uppercase">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        data-testid="input-email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="bg-transparent border-0 border-b border-[#E5E5E5] rounded-none px-0 focus:border-[#1A1A1A] focus:ring-0 text-[#1A1A1A] placeholder:text-[#999999]"
                        placeholder="din@email.dk"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-[#1A1A1A] text-xs tracking-[0.1em] uppercase">Telefon</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        data-testid="input-phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="bg-transparent border-0 border-b border-[#E5E5E5] rounded-none px-0 focus:border-[#1A1A1A] focus:ring-0 text-[#1A1A1A] placeholder:text-[#999999]"
                        placeholder="+45 12 34 56 78"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject" className="text-[#1A1A1A] text-xs tracking-[0.1em] uppercase">Emne *</Label>
                    <Input
                      id="subject"
                      name="subject"
                      data-testid="input-subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="bg-transparent border-0 border-b border-[#E5E5E5] rounded-none px-0 focus:border-[#1A1A1A] focus:ring-0 text-[#1A1A1A] placeholder:text-[#999999]"
                      placeholder="Hvad handler din henvendelse om?"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-[#1A1A1A] text-xs tracking-[0.1em] uppercase">Besked *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="input-message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="bg-transparent border-0 border-b border-[#E5E5E5] rounded-none px-0 focus:border-[#1A1A1A] focus:ring-0 text-[#1A1A1A] placeholder:text-[#999999] resize-none"
                      placeholder="Fortæl os om dit projekt..."
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="submit-btn"
                    disabled={isSubmitting}
                    className="w-full bg-[#1A1A1A] text-white hover:bg-[#333333] font-medium text-xs tracking-[0.15em] uppercase py-6 rounded-none mt-8"
                  >
                    {isSubmitting ? 'Sender...' : 'Send besked'}
                  </Button>
                </form>
              )}
            </div>

            {/* Address Info */}
            <div>
              <h2 className="font-heading text-xl font-medium text-[#1A1A1A] mb-8">
                Besøg os
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <MapPin className="w-5 h-5 text-[#999999] mt-0.5" />
                  <div>
                    <p className="text-[#999999] text-xs tracking-[0.1em] uppercase mb-1">
                      Adresse
                    </p>
                    <a
                      href="https://maps.google.com/?q=Herlev+Hovedgade+195+2730+Herlev"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#1A1A1A] hover:text-[#666666] transition-colors duration-300"
                    >
                      Symfonivej 35<br />
                      2730 Herlev
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Phone className="w-5 h-5 text-[#999999] mt-0.5" />
                  <div>
                    <p className="text-[#999999] text-xs tracking-[0.1em] uppercase mb-1">
                      Telefon
                    </p>
                    <a
                      href="tel:+4536308080"
                      className="text-[#1A1A1A] hover:text-[#666666] transition-colors duration-300"
                    >
                      +45 36 30 80 80
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Mail className="w-5 h-5 text-[#999999] mt-0.5" />
                  <div>
                    <p className="text-[#999999] text-xs tracking-[0.1em] uppercase mb-1">
                      Email
                    </p>
                    <a
                      href="mailto:neonsign@neonsign.dk"
                      className="text-[#1A1A1A] hover:text-[#666666] transition-colors duration-300"
                    >
                      neonsign@neonsign.dk
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
